import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import ReplayIcon from "@mui/icons-material/Replay";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import { useAuth } from "../../../auth/AuthContext";
import { formatDurationHours, formatTimestamp } from "../../cmms/cmmsFormat";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_FIELD_ROLES, CMMS_MANAGER_ROLES, CMMS_TECHNICIAN_ROLES } from "../../cmms/cmmsRoles";
import AxionControls from "../../../components/controls/AxionControls";
import {
  changeWorkOrderStatus,
  checkInWorkOrder,
  checkOutWorkOrder,
  claimWorkOrder,
  getWorkOrderById,
} from "../../../fetchers/cmmsWorkOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import {
  WorkOrderStatus,
  getPriorityBadge,
  getStatusBadge,
  getTypeBadge,
  getPermittedWorkOrderStatusOptions,
} from "../workOrderEnums";
import {
  cmmsTypeScale,
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getInputLabelSx,
  getMenuSx,
  getOutlinedButtonSx,
  getPanelTitleSx,
  getSectionBoxSx,
  getSectionLabelSx,
  getSelectSx,
  getStatusChipSx,
  getTabsSx,
  getTypeChipSx,
} from "../../cmms/assetHierarchyStyles";
import WorkOrderTaskList from "./WorkOrderTaskList";
import WorkOrderCommentList from "./WorkOrderCommentList";
import WorkOrderAttachmentList from "./WorkOrderAttachmentList";
import MaterialUsageList from "./MaterialUsageList";
import LaborHourList from "./LaborHourList";
import RelatedDocumentList from "./RelatedDocumentList";
import WorkOrderHistoryList from "./WorkOrderHistoryList";
import ReopenDialog from "./ReopenDialog";
import { CmmsErrorState, CmmsListSkeleton, formatCmmsError } from "../../cmms/components/CmmsStates";

/*
 * A group of details — one bordered box holding pairs that flow.
 *
 * The fields are not a table and stopped pretending to be one. A rigid grid has to size its
 * columns for the widest label and the longest value, which on a panel holding "Skapad" beside
 * "Rapporterat verktyg" wastes most of a row on whitespace. Flowing inline pairs take the width
 * they need and wrap when they run out, which fits three to a row here where a grid fitted two.
 *
 * Grouping is by the question each field answers — what the order is about, who has it, when
 * things happened. The boxes carry no headings: three of them in a row is rhythm, and a heading
 * on each would cost back the height the layout just saved.
 */
const getDetailGroupSx = (cmmsColors) => ({
  ...getSectionBoxSx(cmmsColors),
  display: "flex",
  flexWrap: "wrap",
  columnGap: 3,
  rowGap: 1,
  py: 1.5,
  mb: 1,
});

/*
 * "Label: value" on one line, the pair kept together when the row wraps.
 *
 * Every field renders whether or not it holds anything — an em dash where there is no value. The
 * tool fields below are the one exception and were already: a work order that concerns no tool is
 * the normal case, where an unset planned start is a blank someone may be about to fill in.
 *
 * `span` takes a whole row. Prose belongs there — a description or a cause note read as a
 * paragraph, and a paragraph sharing a row with two timestamps is a column of two-word lines.
 */
function DetailLine({ label, value, cmmsColors, span = false }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0, ...(span ? { flexBasis: "100%" } : {}) }}>
      {/* nowrap, or a two-word label breaks across lines and the pair stops reading as a pair. */}
      <Typography variant="caption" sx={{ color: cmmsColors.mutedText, fontWeight: 700, whiteSpace: "nowrap" }}>
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ color: cmmsColors.text, fontWeight: 600, wordBreak: "break-word" }}>
        {value ?? "—"}
      </Typography>
    </Box>
  );
}

function renderBadge(badge) {
  return (
    <Chip
      label={badge.label}
      size="small"
      sx={{ backgroundColor: badge.bg, color: badge.text, fontWeight: 700 }}
    />
  );
}

export default function WorkOrderDetailsPanel({
  workOrderId,
  companyKey,
  assetNodeNameById,
  userNameById,
  onClose,
  onEdit,
  onEscalate,
  onChanged,
}) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const { roleIds } = useAuth();
  const queryClient = useQueryClient();

  const [statusDraft, setStatusDraft] = useState("");
  const intl = useIntl();
  const opFailed = intl.formatMessage(messages.operationFailed);
  const [tab, setTab] = useState(0);
  const [confirmClaim, setConfirmClaim] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);

  const open = Boolean(workOrderId);

  const query = useQuery(
    ["cmmsWorkOrder", companyKey, workOrderId],
    () => getWorkOrderById(companyKey, workOrderId),
    { enabled: open && Boolean(companyKey), staleTime: 0 },
  );

  const workOrder = query.data;

  useEffect(() => {
    if (workOrder) setStatusDraft(workOrder.status);
  }, [workOrder]);

  useEffect(() => {
    if (!open) setTab(0);
  }, [open]);

  const statusMutation = useMutation({
    mutationFn: (newStatus) => changeWorkOrderStatus(workOrderId, { companyKey, newStatus }),
  });

  const checkInMutation = useMutation({
    mutationFn: () => checkInWorkOrder(workOrderId, { companyKey }),
  });

  const checkOutMutation = useMutation({
    mutationFn: () => checkOutWorkOrder(workOrderId, { companyKey }),
  });

  const claimMutation = useMutation({
    mutationFn: () => claimWorkOrder(workOrderId, { companyKey }),
  });

  // react-query v4 exposes isLoading on mutations; isPending is the v5 name.
  const isMutating = statusMutation.isPending || statusMutation.isLoading
    || checkInMutation.isPending || checkInMutation.isLoading
    || checkOutMutation.isPending || checkOutMutation.isLoading
    || claimMutation.isPending || claimMutation.isLoading;

  const handleSubmitStatus = async () => {
    if (!statusDraft || statusDraft === workOrder?.status) return;
    try {
      await statusMutation.mutateAsync(statusDraft);
      pushNotification(intl.formatMessage(messages.toastStatusUpdated));
      await query.refetch();
      // Completing or closing drops the work order out of /mine, so the sidebar badge has
      // to be rechecked or it keeps counting work that is already done.
      await queryClient.invalidateQueries({ queryKey: ["cmmsMyWorkOrders"] });
      onChanged?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, opFailed));
    }
  };

  const handleCheckIn = async () => {
    try {
      await checkInMutation.mutateAsync();
      pushNotification(intl.formatMessage(messages.toastCheckedIn));
      await query.refetch();
      await queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrderLabor", companyKey, workOrderId] });
      onChanged?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, opFailed));
    }
  };

  const handleCheckOut = async () => {
    try {
      await checkOutMutation.mutateAsync();
      pushNotification(intl.formatMessage(messages.toastCheckedOut));
      await queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrderLabor", companyKey, workOrderId] });
      await query.refetch();
      onChanged?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, opFailed));
    }
  };

  const handleClaim = async () => {
    try {
      await claimMutation.mutateAsync();
      setConfirmClaim(false);
      pushNotification(intl.formatMessage(messages.toastClaimed));
      await queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrderLabor", companyKey, workOrderId] });
      // Claiming sets DispatchedResourceId to me, which is one of the ways /mine matches.
      await queryClient.invalidateQueries({ queryKey: ["cmmsMyWorkOrders"] });
      await query.refetch();
      onChanged?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, opFailed));
    }
  };

  // Web fallback for technicians: claim an Open, unclaimed work order.
  const canClaim = workOrder?.status === WorkOrderStatus.Open && !workOrder?.dispatchedResourceId;

  // Coordinators reopen a finished work order (manager-only transition, reason required).
  const canReopen = workOrder?.status === WorkOrderStatus.Completed || workOrder?.status === WorkOrderStatus.Closed;

  // Mirrors IsEscalationAllowed on the server: a job that is not finished and not already
  // somewhere else. Escalated appears here only for orders written before escalation moved work
  // orders instead of ending them; those are recovered with a status change, not another
  // hand-over.
  const canEscalate = workOrder?.status === WorkOrderStatus.Draft
    || workOrder?.status === WorkOrderStatus.Open
    || workOrder?.status === WorkOrderStatus.InProgress;

  // MVP scope: Tasks and RelatedDocuments are hidden (kept in code, no entry point).
  // Re-enable by removing keys from MVP_HIDDEN_TABS.
  const MVP_HIDDEN_TABS = ["tasks", "relatedDocuments"];
  const allTabs = [
    { key: "tasks", label: intl.formatMessage(messages.tabTasks), render: () => (
      <WorkOrderTaskList workOrderId={workOrderId} companyKey={companyKey} userNameById={userNameById} />
    ) },
    { key: "comments", label: intl.formatMessage(messages.tabComments), render: () => (
      <WorkOrderCommentList workOrderId={workOrderId} companyKey={companyKey} userNameById={userNameById} />
    ) },
    { key: "attachments", label: intl.formatMessage(messages.tabAttachments), render: () => (
      <WorkOrderAttachmentList workOrderId={workOrderId} companyKey={companyKey} userNameById={userNameById} />
    ) },
    { key: "materials", label: intl.formatMessage(messages.tabMaterials), render: () => (
      <MaterialUsageList workOrderId={workOrderId} companyKey={companyKey} userNameById={userNameById} />
    ) },
    { key: "labor", label: intl.formatMessage(messages.tabLabor), render: () => (
      <LaborHourList workOrderId={workOrderId} companyKey={companyKey} userNameById={userNameById} />
    ) },
    { key: "relatedDocuments", label: intl.formatMessage(messages.tabDocuments), render: () => (
      <RelatedDocumentList workOrderId={workOrderId} companyKey={companyKey} />
    ) },
    // Not hidden by MVP_HIDDEN_TABS, unlike Tasks and Documents. Escalation moves the work order
    // between departments and the order itself only remembers where it is now, so this is the
    // only place a hand-over is visible at all.
    { key: "history", label: intl.formatMessage(messages.tabHistory), render: () => (
      <WorkOrderHistoryList workOrderId={workOrderId} companyKey={companyKey} />
    ) },
  ];
  const visibleTabs = allTabs.filter((t) => !MVP_HIDDEN_TABS.includes(t.key));
  const activeTabIndex = Math.min(tab, visibleTabs.length - 1);
  const activeTab = visibleTabs[activeTabIndex];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Wider than it was. The inline pairs fit three to a row here where 600px fitted two, and
      // the list behind is still readable alongside it on any normal screen. Full width on a
      // phone, as before.
      PaperProps={{ sx: { width: { xs: "100%", sm: 720, lg: 860 }, backgroundColor: cmmsColors.surface } }}
    >
      {!workOrder && query.isLoading &&
        <Box sx={{ p: 2.5 }}>
          <CmmsListSkeleton rows={9} height={30} />
        </Box>
      }
      {query.error &&
        <Box sx={{ p: 3 }}>
          <CmmsErrorState
            error={query.error}
            fallback={intl.formatMessage(messages.loadFailed)}
            onRetry={() => query.refetch()}
            isRetrying={query.isFetching}
          />
        </Box>
      }
      {workOrder &&
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ p: 2.5, borderBottom: `1px solid ${cmmsColors.border}` }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              {/*
                * The number beside the title rather than under it. It is an identifier, not a
                * subtitle — someone reads it out on the phone or types it into a search — and as
                * a chip it is both easier to pick out and one line shorter.
                */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", minWidth: 0 }}>
                <Typography variant={cmmsTypeScale.panelTitle} sx={{ ...getPanelTitleSx(cmmsColors), wordBreak: "break-word" }}>
                  {workOrder.title}
                </Typography>
                <Chip size="small" label={`WO #${workOrder.id}`} sx={getTypeChipSx(cmmsColors)} />
              </Stack>
              <IconButton onClick={onClose} aria-label={intl.formatMessage(messages.closePanel)}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
              {renderBadge(getStatusBadge(workOrder.status, intl))}
              {renderBadge(getPriorityBadge(workOrder.priority, intl))}
              {renderBadge(getTypeBadge(workOrder.workOrderType, intl))}
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 2.5 }}>
            <Box>
              <Typography variant={cmmsTypeScale.sectionLabel} sx={{ ...getSectionLabelSx(cmmsColors), mb: 1 }}>
                <FormattedMessage {...messages.sectionDetails} />
              </Typography>
              {/* What the order is about. */}
              <Box sx={getDetailGroupSx(cmmsColors)}>
              <DetailLine
                label={intl.formatMessage(messages.fieldAsset)}
                value={assetNodeNameById?.get(workOrder.assetNodeId) ?? `#${workOrder.assetNodeId}`}
                cmmsColors={cmmsColors}
              />
              {/*
                * The tools. Placed under the asset because that is how they read: what
                * this work order is about, then what was in it.
                *
                * Rendered only when the order names one. An em dash here would read as a broken
                * field, where nothing at all reads as what it is: most work orders concern no
                * tool. The name is resolved server-side onto the view model, so a null one means
                * the tool row has since been deleted — the id is then still the honest answer,
                * as it already is for an asset or a user the panel cannot name.
                */}
              {workOrder.reportedToolId &&
                <DetailLine
                  label={intl.formatMessage(messages.fieldReportedTool)}
                  value={workOrder.reportedToolName ?? `#${workOrder.reportedToolId}`}
                  cmmsColors={cmmsColors}
                />
              }
              {workOrder.toolId &&
                <DetailLine
                  label={intl.formatMessage(messages.fieldTool)}
                  value={workOrder.toolName ?? `#${workOrder.toolId}`}
                  cmmsColors={cmmsColors}
                />
              }
              {/* A row of its own — it is the one field here that is prose. */}
              <DetailLine
                label={intl.formatMessage(messages.fieldDescription)}
                value={workOrder.description}
                cmmsColors={cmmsColors}
                span
              />
              </Box>

              {/* Who has it. */}
              <Box sx={getDetailGroupSx(cmmsColors)}>
              <DetailLine
                label={intl.formatMessage(messages.fieldAssignedDepartmentShort)}
                value={workOrder.assignedDepartmentId ? (assetNodeNameById?.get(workOrder.assignedDepartmentId) ?? `#${workOrder.assignedDepartmentId}`) : "—"}
                cmmsColors={cmmsColors}
              />
              {/*
                * The crew, beside the area. A retired trade says so: the order still names it, but
                * nobody in it hears about the order any more, and a manager reading this panel is
                * the one who can move it on. The chip is a span so it can sit inside the value's
                * paragraph.
                */}
              <DetailLine
                label={intl.formatMessage(messages.fieldTrade)}
                value={workOrder.tradeId
                  ? (
                    <>
                      {workOrder.tradeName ?? `#${workOrder.tradeId}`}
                      {workOrder.tradeIsActive === false &&
                        <Tooltip title={intl.formatMessage(messages.tradeInactiveHint)}>
                          <Chip
                            component="span"
                            size="small"
                            label={intl.formatMessage(messages.tradeInactive)}
                            sx={{ ...getStatusChipSx(cmmsColors, false), ml: 0.75, height: 20 }}
                          />
                        </Tooltip>
                      }
                    </>
                  )
                  : "—"}
                cmmsColors={cmmsColors}
              />
              <DetailLine
                label={intl.formatMessage(messages.fieldAssignedTo)}
                value={workOrder.assignedResourceId ? (userNameById?.get(workOrder.assignedResourceId) ?? `#${workOrder.assignedResourceId}`) : "—"}
                cmmsColors={cmmsColors}
              />
              <DetailLine
                label={intl.formatMessage(messages.fieldDispatchedTo)}
                value={workOrder.dispatchedResourceId ? (userNameById?.get(workOrder.dispatchedResourceId) ?? `#${workOrder.dispatchedResourceId}`) : "—"}
                cmmsColors={cmmsColors}
              />
              </Box>

              {/* When things happened. Six timestamps, which is why they get a box of their own —
                  interleaved with the names above they were most of the panel's height. The
                  estimate sits with the planned dates: it is the rest of the plan, set beside
                  what the labour tab records as actually spent. */}
              <Box sx={getDetailGroupSx(cmmsColors)}>
              <DetailLine label={intl.formatMessage(messages.fieldPlannedStart)} value={formatTimestamp(intl, workOrder.plannedStartDate)} cmmsColors={cmmsColors} />
              <DetailLine label={intl.formatMessage(messages.fieldPlannedEnd)} value={formatTimestamp(intl, workOrder.plannedEndDate)} cmmsColors={cmmsColors} />
              <DetailLine label={intl.formatMessage(messages.fieldEstimatedHours)} value={formatDurationHours(intl, workOrder.estimatedHours)} cmmsColors={cmmsColors} />
              <DetailLine label={intl.formatMessage(messages.fieldActualStart)} value={formatTimestamp(intl, workOrder.actualStartDate)} cmmsColors={cmmsColors} />
              <DetailLine label={intl.formatMessage(messages.fieldActualEnd)} value={formatTimestamp(intl, workOrder.actualEndDate)} cmmsColors={cmmsColors} />
              <DetailLine label={intl.formatMessage(messages.fieldClosedAt)} value={formatTimestamp(intl, workOrder.closedAt)} cmmsColors={cmmsColors} />
              <DetailLine label={intl.formatMessage(messages.fieldCreated)} value={formatTimestamp(intl, workOrder.createdAt)} cmmsColors={cmmsColors} />
              </Box>
            </Box>

            {(workOrder.failureCode || workOrder.failureCauseNote) &&
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant={cmmsTypeScale.sectionLabel} sx={{ ...getSectionLabelSx(cmmsColors), mb: 1 }}>
                  <FormattedMessage {...messages.sectionFailure} />
                </Typography>
                <Box sx={getDetailGroupSx(cmmsColors)}>
                  <DetailLine label={intl.formatMessage(messages.fieldFailureCode)} value={workOrder.failureCode} cmmsColors={cmmsColors} />
                  {/* Prose again, and the longer of the two by far. */}
                  <DetailLine label={intl.formatMessage(messages.fieldCauseNote)} value={workOrder.failureCauseNote} cmmsColors={cmmsColors} span />
                </Box>
              </>
            }

            {/*
              * Everything you can do to this work order on one line, in a box of its own.
              *
              * It was two stacked rows — the status change above, the buttons below — with a
              * divider and a heading floating above both. One row in one box reads as one thing,
              * which is what it is. No divider either: the box's own border already separates it
              * from the details above.
              *
              * gap rather than Stack spacing, because this row wraps. Stack's spacing is margin
              * on the following sibling, which gives a wrapped second line no vertical gap at all.
              */}
            <Box sx={{ ...getSectionBoxSx(cmmsColors), py: 1.5, mt: 2 }}>
              <Typography variant={cmmsTypeScale.sectionLabel} sx={{ ...getSectionLabelSx(cmmsColors), mb: 1.25 }}>
                <FormattedMessage {...messages.sectionActions} />
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            <HideIfNoRole anyOf={CMMS_FIELD_ROLES}>
              {/*
                * The select and its Apply stay welded together and wrap as one — split across two
                * lines they would read as a dropdown with no button and a button with nothing to
                * apply. The extra right margin is what separates the status group from the plain
                * actions after it, in place of the row break that used to do that job.
                */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 1 }}>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel id="wo-status-change" sx={getInputLabelSx(cmmsColors)}><FormattedMessage {...messages.changeStatus} /></InputLabel>
                  <Select
                    labelId="wo-status-change"
                    label={intl.formatMessage(messages.changeStatus)}
                    value={statusDraft || ""}
                    onChange={(event) => setStatusDraft(event.target.value)}
                    sx={getSelectSx(cmmsColors)}
                    MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
                  >
                    {getPermittedWorkOrderStatusOptions(intl, roleIds, workOrder.status).map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/*
                  * The module's own button styling on all of these, not MUI's default.
                  * palette.primary.main is colors.primary[500], which is also the drawer's own
                  * surface, so a bare contained button paints itself the colour of what is behind
                  * it. getContainedButtonSx also carries the disabled treatment, which matters
                  * here: Apply spends most of its life disabled.
                  */}
                <Button
                  variant="contained"
                  onClick={handleSubmitStatus}
                  disabled={!statusDraft || statusDraft === workOrder.status || isMutating}
                  sx={getContainedButtonSx(cmmsColors)}
                >
                  <FormattedMessage {...messages.apply} />
                </Button>
              </Box>
            </HideIfNoRole>
              {/* Both open a dialog the caller owns, so a caller that supplies no handler —
                  the machine page's maintenance tab, which has no room for the form dialogs — must not be
                  offered the button. They were previously rendered unconditionally with
                  `onEdit?.()`, which would have been a dead control there. */}
              {onEdit &&
                <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                  <Button startIcon={<EditIcon />} variant="outlined" onClick={() => onEdit(workOrder)} sx={getOutlinedButtonSx(cmmsColors)}>
                    <FormattedMessage {...cmmsMessages.edit} />
                  </Button>
                </HideIfNoRole>
              }
              {/* Technicians, not managers. Escalating is a diagnostic conclusion — "this is not
                  mechanical, electrical need to look at it" — and the person holding it is the
                  technician who has just spent an hour on the machine. It is hidden on a finished
                  job because the backend refuses those: a Completed or Closed order is reopened
                  first, which is the manager's call and has a dialog of its own. */}
              {onEscalate && canEscalate &&
                <HideIfNoRole anyOf={CMMS_TECHNICIAN_ROLES}>
                  <Button startIcon={<TrendingUpIcon />} variant="outlined" onClick={() => onEscalate(workOrder)} sx={getOutlinedButtonSx(cmmsColors)}>
                    <FormattedMessage {...cmmsMessages.actionEscalate} />
                  </Button>
                </HideIfNoRole>
              }
              {canReopen &&
                <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                  <Button startIcon={<ReplayIcon />} variant="contained" onClick={() => setReopenOpen(true)} sx={getContainedButtonSx(cmmsColors)}>
                    <FormattedMessage {...cmmsMessages.actionReopen} />
                  </Button>
                </HideIfNoRole>
              }
              {/* Claim and labour hours are technician work — the endpoints are
                  Cmms_Technician, so showing these to an operator only produces a 403. */}
              {canClaim &&
                <HideIfNoRole anyOf={CMMS_TECHNICIAN_ROLES}>
                  <Button startIcon={<AssignmentIndIcon />} variant="contained" onClick={() => setConfirmClaim(true)} disabled={isMutating} sx={getContainedButtonSx(cmmsColors)}>
                    <FormattedMessage {...cmmsMessages.actionClaim} />
                  </Button>
                </HideIfNoRole>
              }
              {workOrder.status === WorkOrderStatus.InProgress &&
                <HideIfNoRole anyOf={CMMS_TECHNICIAN_ROLES}>
                  {/*
                    * The house green, and the only accent in the row. Clocking on is what a
                    * technician opened this panel to do; the rest of the buttons are things they
                    * might do. Check-out stays plain so the pair does not read as two equal
                    * choices — see the accent tone in assetHierarchyStyles.
                    */}
                  <Button startIcon={<LoginIcon />} variant="outlined" onClick={handleCheckIn} disabled={isMutating} sx={getOutlinedButtonSx(cmmsColors, "accent")}>
                    <FormattedMessage {...cmmsMessages.actionCheckIn} />
                  </Button>
                  <Button startIcon={<LogoutIcon />} variant="outlined" onClick={handleCheckOut} disabled={isMutating} sx={getOutlinedButtonSx(cmmsColors)}>
                    <FormattedMessage {...cmmsMessages.actionCheckOut} />
                  </Button>
                </HideIfNoRole>
              }
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />
            <Tabs value={activeTabIndex} onChange={(_event, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={getTabsSx(cmmsColors, { fitLabels: true })}>
              {visibleTabs.map((t) => (
                <Tab disableRipple key={t.key} label={t.label} />
              ))}
            </Tabs>
            <Box sx={{ mt: 2 }}>
              {activeTab?.render()}
            </Box>
          </Box>

          <AxionControls.AxionConfirmation
            open={confirmClaim}
            title={intl.formatMessage(messages.claimConfirmTitle)}
            message={intl.formatMessage(messages.claimConfirmMessage)}
            confirmText={intl.formatMessage(cmmsMessages.actionClaim)}
            cancelText={intl.formatMessage(cmmsMessages.cancel)}
            onConfirm={handleClaim}
            onCancel={() => setConfirmClaim(false)}
          />

          <ReopenDialog
            open={reopenOpen}
            workOrderId={workOrderId}
            onClose={() => setReopenOpen(false)}
            onSuccess={async () => {
              await query.refetch();
              onChanged?.();
            }}
          />
        </Box>
      }
    </Drawer>
  );
}
